/**
 * Demo Scenario Injection — standalone page at /demo-scenarios
 *
 * Deliberately NOT a tab inside the Admin Console, and deliberately outside
 * MainLayout (no sidebar, no app header). The whole point of this screen is to
 * be driven from a SECOND browser tab while the app being demonstrated sits
 * untouched in the first: inject a scenario here, switch tabs, refresh the
 * Anomalies list. Living inside the console meant navigating the demo app away
 * from wherever you were mid-story just to stage the next finding.
 *
 * It is still behind ProtectedRoute + admin role, and every request still
 * carries the real tenant id and bearer token - being unchromed doesn't mean
 * being unauthenticated.
 */

import React from 'react';
import { FlaskConical } from 'lucide-react';
import { DemoScenariosTab } from '../components/admin/DemoScenariosTab';

const DemoScenariosPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Its own minimal header, since MainLayout's isn't wrapping this page. */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <FlaskConical className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold tracking-tight text-foreground">
              Demo Scenario Injection
            </h1>
            <p className="text-xs text-muted-foreground">
              Stage anomalies for a live demo — keep this open in a second tab
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-6">
        <div className="mb-5 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-xs leading-relaxed text-amber-600 dark:text-amber-400">
          <p className="font-semibold">Two of the six scenarios are hidden by the default list filter.</p>
          <p className="mt-1">
            <strong>Cost drift</strong> (56% increase) falls below the 100% floor, and{' '}
            <strong>New service</strong> is excluded by type. Both are injected correctly — click{' '}
            <em>Show all findings</em> on the Anomalies page to see them. Prefer{' '}
            <strong>Service-level spike</strong> for a demo: it is the only scenario whose metadata
            sets detector votes, so it also produces a multi-signal-confirmed badge and a
            deterministically-anchored root cause.
          </p>
        </div>

        <DemoScenariosTab />
      </main>
    </div>
  );
};

export default DemoScenariosPage;
